/**
 * Product Apps Web Component
 * 
 * This custom element searches for shopify-app-block elements within .product__meta
 * and moves them to the corresponding app containers based on matching IDs.
 * Now supports multiple product-apps elements, each with its own data-app attribute.
 */
class ProductApps extends HTMLElement {
  constructor() {
    super();
    this.processedBlocks = new Set();
    this.appName = null;
    
    // Configuration for elements that should hide the container
    // Easy to modify for future changes
    this.hiddenElementConfig = {
      selector: '.jdgm-prev-badge[data-average-rating="0.00"]',
      hideClass: 'hidden'
    };
  }

  connectedCallback() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.setupAppName();
    this.moveAppBlocks();
  }

  /**
   * Setup app name from the data-app attribute of this element
   */
  setupAppName() {
    this.appName = this.getAttribute('data-app');
    
    if (!this.appName) {
      console.warn('ProductApps: No data-app attribute found on product-apps element');
      return;
    }
  }

  /**
   * Find and move shopify-app-block elements to this container
   */
  moveAppBlocks() {
    if (!this.appName) {
      return;
    }

    const productMeta = document.querySelector('.product__meta');
    
    if (!productMeta) {
      console.warn('ProductApps: .product__meta container not found');
      return;
    }

    const appBlocks = productMeta.querySelectorAll('.shopify-app-block');
    
    appBlocks.forEach(block => {
      if (this.processedBlocks.has(block)) {
        return; // Skip already processed blocks
      }

      const blockId = block.id;
      if (!blockId) {
        console.warn('ProductApps: shopify-app-block found without ID:', block);
        return;
      }

      // Check if this block matches this app container
      if (this.isMatchingBlock(blockId)) {
        this.moveBlockToContainer(block);
        this.processedBlocks.add(block);
      }
    });
  }

  /**
   * Check if a block ID matches this app container
   * @param {string} blockId - The ID of the shopify-app-block
   * @returns {boolean} - Whether the block matches this app container
   */
  isMatchingBlock(blockId) {
    return blockId.toLowerCase().includes(this.appName.toLowerCase());
  }

  /**
   * Move a block to this container
   * @param {HTMLElement} block - The shopify-app-block element
   */
  moveBlockToContainer(block) {
    try {
      // Check for specific elements that should hide the entire container
      this.checkForHiddenElements(block);

      // Clear the container's text content if it only contains the app name
      if (this.textContent.trim() === this.appName) {
        this.textContent = '';
      }

      // Move the block to this container
      this.appendChild(block);
      
      // Add a class to indicate this container now has content
      this.classList.add('has-app-content');      
    } catch (error) {
      console.error('ProductApps: Error moving block to container:', error);
    }
  }

  /**
   * Check for specific elements within the block that should hide the entire container
   * @param {HTMLElement} block - The shopify-app-block element to check
   */
  checkForHiddenElements(block) {
    try {
      const hiddenElement = block.querySelector(this.hiddenElementConfig.selector);
      
      if (hiddenElement) {
        // Add the hide class to this product-apps container
        this.classList.add(this.hiddenElementConfig.hideClass);
        
        console.log(`ProductApps: Found ${this.hiddenElementConfig.selector} in block, added ${this.hiddenElementConfig.hideClass} class to container`);
      }
    } catch (error) {
      console.error('ProductApps: Error checking for hidden elements:', error);
    }
  }

  /**
   * Method to re-scan for new app blocks (useful for dynamic content)
   */
  rescan() {
    this.processedBlocks.clear();
    this.moveAppBlocks();
  }

  /**
   * Get the app name for this container
   * @returns {string|null} - The app name or null
   */
  getAppName() {
    return this.appName;
  }

  /**
   * Get the number of processed blocks
   * @returns {number} - Number of processed blocks
   */
  getProcessedBlockCount() {
    return this.processedBlocks.size;
  }
}

// Register the custom element
customElements.define('product-apps', ProductApps);

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductApps;
}
